import {
  TimeInForce,
  SelfTradePrevention,
  OrderType,
  INTERNAL_SYMBOL,
} from '@katanaperps/katana-perps-sdk';
import { v1 as uuidv1 } from 'uuid';

import { TypeDataSignFn } from 'lib/ethereum/sign/typedData/eip712';

import { getChainPrivateApi } from './apiCoreSdk';

import type {
  RestRequestOrderTypeMarket,
  RestRequestOrderTypeLimit,
  RestRequestOrder,
  OrderSide,
} from '@katanaperps/katana-perps-sdk';

export type BaseCreateOrderType = {
  walletAddress: string;
  delegatedKeyWallet: string | undefined;
  baseSymbol: string;
  quoteSymbol: string;
  side: OrderSide;
  quantity: string;
  signTypedData: TypeDataSignFn;
  reduceOnly: boolean;
};

type TPSLOrders = {
  takeProfit?: RestRequestOrder & { type: typeof OrderType.takeProfitMarket };
  stopLoss?: RestRequestOrder & { type: typeof OrderType.stopLossMarket };
};

/**
 * FOK orders must specify the CN self-trade prevention policy to be accepted by the matching engine.
 * See API docs for more information.
 */
const determineSelftTradePrevention = (
  timeInForce?: TimeInForce
): typeof SelfTradePrevention.cn | typeof SelfTradePrevention.dc =>
  timeInForce === TimeInForce.fok
    ? SelfTradePrevention.cn
    : SelfTradePrevention.dc;

export const prepareCreateLimitOrder = async (
  {
    delegatedKeyWallet,
    walletAddress,
    baseSymbol,
    quoteSymbol,
    type,
    price,
    side,
    quantity,
    timeInForce,
    signTypedData,
    reduceOnly,
  }: BaseCreateOrderType &
    Pick<RestRequestOrderTypeLimit, 'price' | 'type' | 'timeInForce'>,
  tpSlOrders: TPSLOrders | undefined
) => {
  const params = {
    delegatedKey: delegatedKeyWallet,
    nonce: uuidv1(),
    wallet: walletAddress,
    market: `${baseSymbol}-${quoteSymbol}`,
    side,
    type,
    quantity,
    price,
    timeInForce,
    selfTradePrevention: determineSelftTradePrevention(timeInForce),
    reduceOnly,
  } satisfies RestRequestOrderTypeLimit;
  console.debug(params);

  if (tpSlOrders?.takeProfit || tpSlOrders?.stopLoss) {
    return getChainPrivateApi()
      [INTERNAL_SYMBOL].placeOrderWithConditionalTpSlOrders(
        {
          conditionalTakeProfitOrder: tpSlOrders.takeProfit,
          conditionalStopLossOrder: tpSlOrders.stopLoss,
          order: params,
        },
        signTypedData
      )
      .then(order => order.order);
  }

  return getChainPrivateApi().createOrder(params, signTypedData);
};

type OrderTypeStopLimit = Extract<
  RestRequestOrder,
  { type: typeof OrderType.stopLossLimit | typeof OrderType.takeProfitLimit }
>;

export const prepareCreateLimitStopOrder = async (
  {
    walletAddress,
    baseSymbol,
    quoteSymbol,
    type,
    price,
    side,
    quantity,
    triggerPrice,
    triggerType,
    signTypedData,
    timeInForce,
    delegatedKeyWallet,
    reduceOnly,
  }: BaseCreateOrderType &
    Pick<
      OrderTypeStopLimit,
      'timeInForce' | 'triggerType' | 'triggerPrice' | 'price' | 'type'
    >,
  tpSlOrders: TPSLOrders | undefined
) => {
  const params = {
    delegatedKey: delegatedKeyWallet,
    nonce: uuidv1(),
    wallet: walletAddress,
    market: `${baseSymbol}-${quoteSymbol}`,
    side,
    type,
    quantity,
    price,
    triggerPrice,
    triggerType,
    timeInForce,
    selfTradePrevention: determineSelftTradePrevention(timeInForce),
    reduceOnly,
  } satisfies OrderTypeStopLimit;

  console.debug(params);

  if (tpSlOrders?.takeProfit || tpSlOrders?.stopLoss) {
    return getChainPrivateApi()
      [INTERNAL_SYMBOL].placeOrderWithConditionalTpSlOrders(
        {
          conditionalTakeProfitOrder: tpSlOrders.takeProfit,
          conditionalStopLossOrder: tpSlOrders.stopLoss,
          order: params,
        },
        signTypedData
      )
      .then(order => order.order);
  }

  return getChainPrivateApi().createOrder(params, signTypedData);
};

type OrderTypeStopMarket = Extract<
  RestRequestOrder,
  { type: typeof OrderType.stopLossMarket | typeof OrderType.takeProfitMarket }
>;

export const getCreateMarketOrderParams = (
  order: BaseCreateOrderType &
    (
      | Pick<OrderTypeStopMarket, 'type' | 'triggerPrice' | 'triggerType'>
      | Pick<RestRequestOrderTypeMarket, 'type'>
    )
) => {
  return order.type === OrderType.stopLossMarket ||
    order.type === OrderType.takeProfitMarket
    ? ({
        delegatedKey: order.delegatedKeyWallet,
        nonce: uuidv1(),
        wallet: order.walletAddress,
        market: `${order.baseSymbol}-${order.quoteSymbol}`,
        side: order.side,
        type: order.type,
        triggerPrice: order.triggerPrice,
        triggerType: order.triggerType,
        quantity: order.quantity,
        reduceOnly: order.reduceOnly,
      } satisfies OrderTypeStopMarket)
    : ({
        delegatedKey: order.delegatedKeyWallet,
        nonce: uuidv1(),
        wallet: order.walletAddress,
        market: `${order.baseSymbol}-${order.quoteSymbol}`,
        side: order.side,
        type: order.type,
        quantity: order.quantity,
        reduceOnly: order.reduceOnly,
      } satisfies RestRequestOrderTypeMarket);
};

export const prepareCreateMarketOrder = async (
  order: BaseCreateOrderType &
    (
      | Pick<OrderTypeStopMarket, 'type' | 'triggerPrice' | 'triggerType'>
      | Pick<RestRequestOrderTypeMarket, 'type'>
    ),
  tpSlOrders: TPSLOrders | undefined
) => {
  const params = getCreateMarketOrderParams(order);

  console.debug(params);

  if (tpSlOrders?.takeProfit || tpSlOrders?.stopLoss) {
    return getChainPrivateApi()
      [INTERNAL_SYMBOL].placeOrderWithConditionalTpSlOrders(
        {
          conditionalTakeProfitOrder: tpSlOrders.takeProfit,
          conditionalStopLossOrder: tpSlOrders.stopLoss,
          order: params,
        },
        order.signTypedData
      )
      .then(order => order.order);
  }

  return getChainPrivateApi().createOrder(params, order.signTypedData);
};
